import React, { useCallback, useEffect, useMemo, useState } from 'react'
import _ from 'lodash'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { InputText } from 'primereact/inputtext'
import { ProgressSpinner } from 'primereact/progressspinner'
import {
  curatedGroupsInCatalog,
  catalogFlowsForType,
  selectableIntegrations,
  supportedTypesForIntegration,
  warnUnmappedFlowCodesOnce
} from './flowTypeCuration'
import ComponentsChip from './ComponentsChip'
import FlowNameChip from './FlowNameChip'
import { chipLimit, integrationLimit } from './flowLimits'
import { chipWithLimit, visualBuilderPickerNote, FLOW_LIMIT_EXPLANATION, VISUAL_BUILDER_ALTERNATIVE_HEADING } from './layoutBuilderCopy'
import { isFlatVisualBuilderSku } from '../../utils/visualBuilderSku'
import { dropOneSuggestions, missingTypesToName, partitionByFullMatch } from './integrationMatching'
import './layoutBuilder.css'

// Check if any flow in the catalog has a flow limit (max_flows).
const hasCatalogFlowLimit = catalog => {
  return catalog.integrations.some(integration =>
    integration.flows && integration.flows.some(flow => flow.max_flows !== null && flow.max_flows !== undefined)
  )
}

const integrationsLabel = count => `${count} integration${count === 1 ? '' : 's'} do${count === 1 ? 'es' : ''}`

// One integration type card: name, picked checkmark, "✓ Supports all" badge for full matches,
// "Missing: …" for near matches, and the curated flow-type chips.
function IntegrationCard ({ integration, applications, generalist, supportedTypes, selectedTypes, isFullMatch, missing, isSelected, onToggle, openChip, onToggleChip, onCloseChip }) {
  // A div, not a button: the chips inside are buttons and buttons cannot nest.
  return (
    <div
      role='option'
      tabIndex={0}
      aria-selected={isSelected}
      className={`layoutBuilder-flowfirst-card ${isSelected ? 'is-selected' : ''}`.trim()}
      onClick={onToggle}
      onKeyDown={event => {
        if (event.target !== event.currentTarget || (event.key !== 'Enter' && event.key !== ' ')) return
        event.preventDefault()
        onToggle()
      }}
    >
      <span className='layoutBuilder-flowfirst-card-top'>
        <span className='layoutBuilder-flowfirst-card-name'>{integration.name}</span>
        <span className='layoutBuilder-flowfirst-card-top-meta'>
          {isFullMatch && <span className='layoutBuilder-flowfirst-card-badge'>✓ Supports all</span>}
          {isSelected && (
            <span className='layoutBuilder-flowfirst-card-check' aria-hidden='true'>✓</span>
          )}
        </span>
      </span>
      {missing.length > 0 && (
        <span className='layoutBuilder-flowfirst-card-missing'>Missing: {missing.join(', ')}</span>
      )}
      {generalist && (
        <span className='layoutBuilder-flowfirst-card-note'>{visualBuilderPickerNote(integrationLimit(integration))}</span>
      )}
      <span className='layoutBuilder-flowfirst-chips'>
        {generalist && (
          <ComponentsChip
            components={integration.components}
            applications={applications}
            open={openChip === `${integration.id}:components`}
            onToggle={() => onToggleChip(`${integration.id}:components`)}
            onClose={onCloseChip}
          />
        )}
        {!generalist && supportedTypes.map(chipName => (
          <FlowNameChip
            key={chipName}
            label={chipWithLimit(chipName, chipLimit(integration, chipName))}
            flows={catalogFlowsForType(integration, chipName)}
            applications={applications}
            isSelected={selectedTypes.has(chipName)}
            open={openChip === `${integration.id}:${chipName}`}
            onToggle={() => onToggleChip(`${integration.id}:${chipName}`)}
            onClose={onCloseChip}
          />
        ))}
      </span>
    </div>
  )
}

// Flow-first Add Integration picker. LEFT: multi-select the curated flow types you want to move
// (discovery/filtering only). RIGHT: every integration type, always shown and clickable. With a
// selection, cards split into those supporting every selected type and the rest; with none, one
// ungrouped list. Picking one calls onAdd with a fixed payload (flows stays empty; the left
// selections never seed it).
export default function AddIntegrationDialog ({ visible, catalog, onHide, onAdd, catalogLoading = false, catalogError = null, catalogRefetch = null }) {
  const [search, setSearch] = useState('')
  const [selectedTypes, setSelectedTypes] = useState(() => new Set())
  const [selectedIntegrationId, setSelectedIntegrationId] = useState(null)
  // Key of the one chip whose detail popover is open (`<integration id>:<curated type>`), if any.
  const [openChip, setOpenChip] = useState(null)
  const closeChip = useCallback(() => setOpenChip(null), [])
  const toggleChip = key => setOpenChip(previous => previous === key ? null : key)

  useEffect(() => {
    if (visible) warnUnmappedFlowCodesOnce(catalog)
  }, [visible, catalog])

  const groups = useMemo(() => curatedGroupsInCatalog(catalog), [catalog])

  // Every selectable integration type (Visual Builder excluded), always present; supported curated
  // types precomputed once per catalog.
  const integrations = useMemo(() =>
    _.sortBy(selectableIntegrations(catalog), integration => integration.name.toLowerCase())
      .map(integration => isFlatVisualBuilderSku(integration.id)
        // Visual Builder is a generalist: it counts as supporting any picked data types (see integrationMatching).
        ? { integration, supportedTypes: [], generalist: true }
        : { integration, supportedTypes: supportedTypesForIntegration(integration) }),
  [catalog])

  const term = search.trim().toLowerCase()
  const filteredGroups = useMemo(() => {
    if (!term) return groups
    return groups
      .map(group => ({ ...group, types: group.types.filter(name => name.toLowerCase().includes(term)) }))
      .filter(group => group.types.length > 0)
  }, [groups, term])

  const selectedCount = selectedTypes.size
  const total = integrations.length

  const { fullMatches, alternatives, others } = useMemo(
    () => partitionByFullMatch(integrations, selectedTypes),
    [integrations, selectedTypes])
  const suggestions = useMemo(
    () => selectedCount > 0 && fullMatches.length === 0 ? dropOneSuggestions(integrations, selectedTypes) : [],
    [integrations, selectedTypes, selectedCount, fullMatches])
  const selected = integrations.find(entry => entry.integration.id === selectedIntegrationId)?.integration

  // Body state: never render the two-panel picker blank. Loading wins over empty (integrations is []
  // until the catalog resolves); error/empty is anything else with nothing to show.
  const showPicker = !catalogLoading && !catalogError && integrations.length > 0
  const showEmptyOrError = !catalogLoading && (catalogError || integrations.length === 0)

  const hide = () => {
    setSearch('')
    setSelectedTypes(new Set())
    setSelectedIntegrationId(null)
    setOpenChip(null)
    onHide()
  }

  const toggleType = flowName => {
    setSelectedTypes(previous => {
      const next = new Set(previous)
      if (next.has(flowName)) next.delete(flowName)
      else next.add(flowName)
      return next
    })
  }

  const renderCards = (entries, label, { isFullMatch }) => (
    <div className='layoutBuilder-flowfirst-integration-grid' role='listbox' aria-label={label}>
      {entries.map(({ integration, supportedTypes, generalist }) => {
        const isSelected = integration.id === selectedIntegrationId
        return (
          <IntegrationCard
            key={integration.id}
            integration={integration}
            applications={catalog?.applications}
            generalist={generalist}
            supportedTypes={supportedTypes}
            selectedTypes={selectedTypes}
            isFullMatch={isFullMatch}
            missing={isFullMatch || selectedCount === 0 ? [] : missingTypesToName(supportedTypes, selectedTypes)}
            isSelected={isSelected}
            onToggle={() => setSelectedIntegrationId(isSelected ? null : integration.id)}
            openChip={openChip}
            onToggleChip={toggleChip}
            onCloseChip={closeChip}
          />
        )
      })}
    </div>
  )

  const add = () => {
    if (!selected) return
    onAdd({
      // Duplicate names remain independent because identity never derives from the label.
      integration_uuid: crypto.randomUUID(),
      integration_type_id: selected.id,
      integration_type_label: selected.name,
      // No inline name field: default to the type label; renaming happens on the integration tab.
      // Left-panel flow selections never seed flows.
      name: selected.name,
      flows: []
    })
    hide()
  }

  return (
    <Dialog
      visible={visible}
      header='Add integration'
      modal
      className='layoutBuilder-add-dialog layoutBuilder-flowfirst-dialog'
      onHide={hide}
      footer={(
        <div className='layoutBuilder-dialog-actions'>
          <span className='layoutBuilder-flowfirst-continue-status' data-testid='flowfirst-continue-status'>
            {selected ? <>Selected: <strong>{selected.name}</strong></> : 'No integration selected yet.'}
          </span>
          <span className='layoutBuilder-form-spacer' />
          <Button label='Cancel' severity='secondary' outlined onClick={hide} />
          <Button label='Add integration' disabled={!selected} onClick={add} />
        </div>
      )}
    >
      <p className='layoutBuilder-flowfirst-intro text-muted'>
        Start by telling us what data you need to move — or just pick an integration directly. Every
        integration type is always available on the right; choosing what to send or receive on the left
        only highlights and ranks the best matches.
      </p>
      {catalogLoading && (
        <div className='layoutBuilder-flowfirst-status' role='status'>
          <ProgressSpinner style={{ width: '2.5rem', height: '2.5rem' }} strokeWidth='4' aria-label='Loading integrations' />
          <p>Loading integrations…</p>
        </div>
      )}
      {showEmptyOrError && (
        <div className='layoutBuilder-flowfirst-status'>
          <p>{catalogError ? "Couldn't load integrations — try again." : 'No integrations are available.'}</p>
          {catalogError && catalogRefetch && (
            <Button label='Retry' icon='pi pi-refresh' onClick={catalogRefetch} />
          )}
        </div>
      )}
      {showPicker && (
        <div className='layoutBuilder-flowfirst-panels'>
          <div className='layoutBuilder-flowfirst-flow-panel' aria-label='What data to move'>
            <h3 className='layoutBuilder-flowfirst-flow-title'>What data do you need to move?</h3>
            <p className='layoutBuilder-flowfirst-flow-sub'>
              Pick what you want to send or receive — we'll match the integrations that support it. Or just
              choose an integration on the right.
            </p>
            <label className='layoutBuilder-visually-hidden' htmlFor='flowfirst-search'>Search</label>
            <InputText
              id='flowfirst-search'
              className='layoutBuilder-flowfirst-search'
              value={search}
              placeholder='Search…'
              aria-label='Search'
              onChange={event => setSearch(event.target.value)}
            />
            <div className='layoutBuilder-flowfirst-selection-bar'>
              <span>{selectedCount} selected</span>
              <button
                type='button'
                className='layoutBuilder-flowfirst-clear'
                disabled={selectedCount === 0}
                onClick={() => setSelectedTypes(new Set())}
              >Clear
              </button>
            </div>
            <div className='layoutBuilder-flowfirst-flow-groups'>
              {filteredGroups.map(group => (
                <div className='layoutBuilder-flowfirst-flow-group' key={group.section}>
                  <h4 className='layoutBuilder-flowfirst-flow-group-heading'>{group.label}</h4>
                  <div className='layoutBuilder-flowfirst-flow-list'>
                    {group.types.map(typeName => (
                      <label className='layoutBuilder-flowfirst-flow-row' key={typeName}>
                        <input
                          type='checkbox'
                          value={typeName}
                          checked={selectedTypes.has(typeName)}
                          onChange={() => toggleType(typeName)}
                        />
                        <span>{typeName}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
              {filteredGroups.length === 0 && (
                <p className='layoutBuilder-flowfirst-flow-empty'>No matches for "{search.trim()}".</p>
              )}
            </div>
          </div>

          <div className='layoutBuilder-flowfirst-integration-panel' aria-label='Integration types'>
            <div className='layoutBuilder-flowfirst-integration-header'>
              <h3>Integration types</h3>
              <span className='layoutBuilder-flowfirst-integration-count' data-testid='flowfirst-match-count'>
                {selectedCount > 0 ? `${fullMatches.length} of ${total} support everything you picked` : `${total} available`}
              </span>
            </div>
            {hasCatalogFlowLimit(catalog) && (
              <p className='layoutBuilder-flowfirst-integration-explanation'>
                {FLOW_LIMIT_EXPLANATION}
              </p>
            )}
            <div className='layoutBuilder-flowfirst-integration-body'>
              {selectedCount === 0 && renderCards(others, 'Integration types', { isFullMatch: false })}
              {selectedCount > 0 && fullMatches.length > 0 && (
                <div className='layoutBuilder-flowfirst-integration-section'>
                  <h4 className='layoutBuilder-flowfirst-integration-section-heading'>
                    Supports everything you picked ({fullMatches.length})
                  </h4>
                  {renderCards(fullMatches, 'Supports everything you picked', { isFullMatch: true })}
                </div>
              )}
              {selectedCount > 0 && fullMatches.length === 0 && (
                <div className='layoutBuilder-flowfirst-no-match' data-testid='flowfirst-no-full-match'>
                  <p className='layoutBuilder-flowfirst-no-match-title'>No single integration covers all {selectedCount}.</p>
                  <ul className='layoutBuilder-flowfirst-no-match-list'>
                    {suggestions.map(({ flowType, count }) => (
                      <li key={flowType}>
                        <button type='button' onClick={() => toggleType(flowType)}>
                          Without "{flowType}", {integrationsLabel(count)}
                        </button>
                      </li>
                    ))}
                    {suggestions.length === 0 && (
                      <li>No single flow type is the sole blocker — several would need to change.</li>
                    )}
                  </ul>
                </div>
              )}
              {selectedCount > 0 && alternatives.length > 0 && (
                <div className='layoutBuilder-flowfirst-integration-section'>
                  <h4 className='layoutBuilder-flowfirst-integration-section-heading'>{VISUAL_BUILDER_ALTERNATIVE_HEADING}</h4>
                  {renderCards(alternatives, VISUAL_BUILDER_ALTERNATIVE_HEADING, { isFullMatch: true })}
                </div>
              )}
              {selectedCount > 0 && (
                <div className='layoutBuilder-flowfirst-integration-section is-other'>
                  <h4 className='layoutBuilder-flowfirst-integration-section-heading'>
                    Other integrations ({others.length})
                  </h4>
                  {others.length > 0
                    ? renderCards(others, 'Other integrations', { isFullMatch: false })
                    : <p className='layoutBuilder-flowfirst-integration-section-empty'>Nothing else to show.</p>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Dialog>
  )
}
